/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction } from '@reduxjs/toolkit';

import { FeatureFlag, FeatureFlags } from '../../../../ts/feature-flags';
import { features } from '../../util/config';
import { getInstance } from '../instance/actions';

type FeaturesSlice = {
  initialized: boolean;
  updating: boolean;
  rules: Readonly<Record<FeatureFlag, boolean>>;
};

const initialState: FeaturesSlice = {
  initialized: false, // Have the rules been initialized?
  updating: false, // Are the rules being updated?
  rules: Object.freeze(features || ({} as FeatureFlags)) // what are the current status of the rules ('rule-name': true/false)?
};

const fetchInstanceInfoStart = (state: FeaturesSlice) => ({
  ...state,
  initialized: false,
  updating: true
});

const fetchInstanceInfoSuccess = (state: FeaturesSlice, action: AnyAction) => {
  const { featureFlags } = action.payload;

  // Combine the default feature flags (i.e. the ones loaded from the configuration file) with the instance-specific
  // flags received in the action. Make sure this object is immutable, so that once set, flag values cannot be
  // changed by accident.
  const rules = Object.freeze({
    ...state.rules,
    ...featureFlags
  });

  return {
    ...state,
    initialized: true,
    updating: false,
    rules
  };
};

const fetchInstanceInfoError = (state: FeaturesSlice) => ({
  ...state,
  initialized: true,
  updating: false,
  rules: Object.freeze({})
});

export const reducer = (state = initialState, action: AnyAction) => {
  switch (action.type) {
    case getInstance.pending.type:
      return fetchInstanceInfoStart(state);
    case getInstance.fulfilled.type:
      return fetchInstanceInfoSuccess(state, action);
    case getInstance.rejected.type:
      return fetchInstanceInfoError(state);
    default:
      return state;
  }
};

export default reducer;
